import {
  FLIGHT_TYPES,
  SOUL_ROLES,
  SOUL_AFFILIATIONS,
  isExcludedAffiliation,
  FlightTypeCode,
  SoulRoleCode,
  SoulAffiliationCode,
} from '@/lib/flightTypes';
import { inputClass } from '@/components/ui';

/**
 * One soul aboard, as both the new-flight form and the flight page edit them.
 *
 * Every person physically on the aeroplane is a row here — the pilot and the
 * instructor included. Whether they count toward BA Law's headcount is decided
 * by the API from `affiliation`, never here: this file renders the record, and
 * `api/src/services/souls.ts` reads it.
 */
export interface ManifestEntry {
  initials: string;
  full_name: string;
  relationship: string;
  category: FlightTypeCode;
  role_aboard: SoulRoleCode;
  affiliation: SoulAffiliationCode;
}

/** "Excluded from the count" is a fact about the record, never a reason to hide it. */
export function ExcludedChip({ affiliation }: { affiliation: string }) {
  if (!isExcludedAffiliation(affiliation)) return null;
  return (
    <span
      className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-300 ring-1 ring-inset ring-amber-500/30"
      title="On the manifest, but outside BA Law's headcount. Name them in the Notes for this leg."
    >
      not in headcount
    </span>
  );
}

export function ManifestRowFields({
  row,
  onChange,
  onRemove,
}: {
  row: ManifestEntry;
  onChange: <K extends keyof ManifestEntry>(key: K, value: ManifestEntry[K]) => void;
  onRemove?: () => void;
}) {
  return (
    <div className="space-y-2">
      <div className="grid gap-2 sm:grid-cols-[1fr_1.6fr_1.6fr_auto]">
        <input
          placeholder="Initials"
          aria-label="Initials"
          value={row.initials}
          onChange={(e) => onChange('initials', e.target.value.toUpperCase())}
          className={inputClass}
        />
        <input
          placeholder="Full name"
          aria-label="Full name"
          value={row.full_name}
          onChange={(e) => onChange('full_name', e.target.value)}
          className={inputClass}
        />
        <input
          placeholder="Relationship (optional)"
          aria-label="Relationship"
          value={row.relationship}
          onChange={(e) => onChange('relationship', e.target.value)}
          className={inputClass}
        />
        {onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            className="rounded-lg px-2 text-slate-500 hover:bg-white/5 hover:text-red-300"
            aria-label="Remove from manifest"
          >
            ✕
          </button>
        ) : (
          <span />
        )}
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        <select
          aria-label="Role aboard"
          title="What they were doing aboard"
          value={row.role_aboard}
          onChange={(e) => onChange('role_aboard', e.target.value as SoulRoleCode)}
          className={inputClass}
        >
          {SOUL_ROLES.map((r) => (
            <option key={r.code} value={r.code} className="bg-night-800">
              {r.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Relationship to the operation"
          title="What they were to this flight — this is what decides the headcount"
          value={row.affiliation}
          onChange={(e) => onChange('affiliation', e.target.value as SoulAffiliationCode)}
          className={inputClass}
        >
          {SOUL_AFFILIATIONS.map((a) => (
            <option key={a.code} value={a.code} className="bg-night-800">
              {a.label}
              {a.excluded ? ' — not counted' : ''}
            </option>
          ))}
        </select>
        <select
          aria-label="Seat purpose"
          title="The purpose of THIS person's seat — usually the flight's own"
          value={row.category}
          onChange={(e) => onChange('category', e.target.value as FlightTypeCode)}
          className={inputClass}
        >
          {FLIGHT_TYPES.map((t) => (
            <option key={t.code} value={t.code} className="bg-night-800">
              {t.code} · {t.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
